import { NotificationListData } from '@shared/types/notification-list-data';
import { Badge } from '@web/components/ui/badge';
import { Button } from '@web/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@web/components/ui/card';
import { ScrollArea } from '@web/components/ui/scroll-area';
import { ViewContainer } from '@web/components/ui/view-container';
import { useListNotifications } from '@web/hooks/notification';
import { cn } from '@web/lib/shadcn';
import { trpc } from '@web/lib/trpc';
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';

const MAX_DESCRIPTION_LENGTH = 400;

const NotificationsList = () => {
  const [notifications, setNotifications] = useState<NotificationListData[]>(
    [],
  );
  const [offset, setOffset] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [expandedNotifications, setExpandedNotifications] = useState<string[]>(
    [],
  );

  const { data, isLoading } = useListNotifications(offset);

  const markNotificationAsRead =
    trpc.notification.markNotificationAsRead.useMutation();

  useEffect(() => {
    if (isLoading || !data) return;

    setNotifications((prevNotifications) => [
      ...prevNotifications,
      ...data.map((n) => ({
        ...n,
        description: n.description,
        createdAt: new Date(n.createdAt),
        parentRecordId: n.parentTableId,
        parentTable: n.parentTable,
        status: n.status,
      })),
    ]);
    const unread = data.filter((n) => n.status === 'new').length;
    setUnreadCount(unread);
  }, [data, isLoading]);

  const loadMore = () => {
    setOffset((prev) => prev + 10);
  };

  const handleMarkAsRead = async (notificationId: string) => {
    await markNotificationAsRead.mutateAsync({ id: notificationId });
    setNotifications((prevNotifications) =>
      prevNotifications.map((n) =>
        n.id === notificationId ? { ...n, isRead: true } : n,
      ),
    );
    setUnreadCount((count) => (count > 0 ? count - 1 : 0));
  };

  const toggleNotificationExpand = (notificationId: string) => {
    setExpandedNotifications((prev) =>
      prev.includes(notificationId)
        ? prev.filter((id) => id !== notificationId)
        : [...prev, notificationId],
    );
  };

  const renderNotificationDescription = (
    notification: NotificationListData,
  ) => {
    const isExpanded = expandedNotifications.includes(notification.id);

    if (!notification.description) {
      return 'No description';
    }

    if (notification.description.length <= MAX_DESCRIPTION_LENGTH) {
      return notification.description;
    }

    return (
      <>
        {isExpanded ? (
          <ScrollArea>{notification.description}</ScrollArea>
        ) : (
          `${notification.description.slice(0, MAX_DESCRIPTION_LENGTH)}...`
        )}

        <Button
          onClick={() => {
            toggleNotificationExpand(notification.id);
          }}
          className="text-primary px-0"
          aria-expanded={isExpanded}
          aria-controls={`description-${notification.id}`}
          variant="link"
        >
          {isExpanded ? 'See Less' : 'See More'}
          {isExpanded ? (
            <ChevronUpIcon className="ml-1 inline-block h-4 w-4" />
          ) : (
            <ChevronDownIcon className="ml-1 inline-block h-4 w-4" />
          )}
        </Button>
      </>
    );
  };

  return (
    <ViewContainer title="Notifications">
      <Card className="w-full border-none shadow-none">
        {unreadCount > 0 && (
          <CardHeader>
            <CardTitle className="text-lg font-medium">
              <Badge variant="default" className="mr-2">
                {unreadCount} new
              </Badge>
            </CardTitle>
          </CardHeader>
        )}
        <CardContent className="p-0">
          {notifications.length > 0 ? (
            <ul className="space-y-4">
              {notifications.map((notification) => (
                <li
                  key={notification.id}
                  className={cn(
                    'shadow-floating rounded-md border p-4 transition-colors',
                    notification.status === 'new'
                      ? 'border-green-200 bg-green-50/50'
                      : 'border-gray-100 bg-white',
                  )}
                >
                  <div className="flex items-start gap-4">
                    <button
                      onClick={() => {
                        void handleMarkAsRead(notification.id);
                      }}
                      disabled={notification.status !== 'new'}
                      className={cn(
                        'mt-1 h-5 w-5 rounded-full border transition-colors',
                        notification.status === 'new'
                          ? 'border-green-500 bg-white hover:bg-green-50'
                          : 'cursor-default border-transparent bg-green-500',
                      )}
                      title={
                        notification.status === 'new'
                          ? 'Mark as read'
                          : 'Already read'
                      }
                    >
                      {notification.status !== 'new' && (
                        <CheckIcon className="mx-auto h-3 w-3 text-white" />
                      )}
                    </button>

                    <div className="flex-1 space-y-1">
                      <p
                        className={cn(
                          'font-medium leading-none',
                          notification.status === 'new'
                            ? 'text-green-900'
                            : 'text-gray-900',
                        )}
                      >
                        {notification.title}
                      </p>
                      <p className="text-sm leading-relaxed text-gray-500">
                        {renderNotificationDescription(notification)}
                      </p>
                      <p className="flex items-center text-xs text-gray-400">
                        <ClockIcon className="mr-1.5 h-3.5 w-3.5" />
                        {new Date(notification.createdAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="py-8 text-center text-gray-500">No notifications.</p>
          )}

          {notifications.length % 10 === 0 && notifications.length > 0 && (
            <div className="mt-6 flex justify-center">
              <Button onClick={loadMore} variant="outline" className="text-sm">
                Load more
                <ChevronDownIcon className="ml-2 h-4 w-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </ViewContainer>
  );
};

export default NotificationsList;
